import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, DollarSign, TrendingUp, Briefcase, BarChart3, FileText, Send } from "lucide-react";
import EntityLink from "../components/shared/EntityLink";
import EntityNotes from "../components/shared/EntityNotes";
import EntityComms from "../components/shared/EntityComms";
import { Entity360Shell, MetricCard } from "../components/shared/OSComponents";
import { fmtUSD } from "../utils/currency";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Fund360Page({ fundId, onBack, breadcrumbs = [] }) {
  const { navigateToEntity } = usePageNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/entity/fund/${fundId}`, { headers })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [fundId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;
  if (!data) return <div className="text-center py-20 text-sm text-[#888]" data-testid="fund-not-found">Fund not found</div>;

  const { entity, kpis: kpiData, relationships, timeline, financials, permissions } = data;
  const crumbs = [...breadcrumbs, { type: "fund", id: fundId, label: entity.name || entity.fund_name || `Fund ${fundId.slice(0, 8)}` }];

  const relatedEntities = [
    ...(relationships.deals || []).map(d => ({ type: "deal", id: d.entity_id, title: d.label, subtitle: d.sublabel })),
    ...(relationships.commitments || []).map(c => ({ type: "commitment", id: c.entity_id, title: c.label, subtitle: c.sublabel })),
    ...(relationships.distributions || []).map(d => ({ type: "distribution", id: d.entity_id, title: d.label, subtitle: d.sublabel })),
  ];

  const targetDollars = (kpiData.target_size || 0) / 100;
  const currentDollars = (kpiData.current_size_cents || 0) / 100;
  const pct = kpiData.target_size ? ((kpiData.current_size_cents || 0) / kpiData.target_size * 100).toFixed(1) : 0;

  const kpis = [
    { label: "Target Size", value: fmtUSD(targetDollars), icon: DollarSign, accent: true },
    { label: "Current Size", value: fmtUSD(currentDollars), icon: TrendingUp },
    { label: "Funded", value: `${pct}%`, icon: BarChart3 },
    { label: "Status", value: (kpiData.status || "—").replace(/_/g, " "), icon: Briefcase },
  ];

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "deals", label: `Deals (${(relationships.deals || []).length})` },
    { key: "timeline", label: `Timeline (${timeline.length})` },
    { key: "comms", label: "Communications" },
    { key: "notes", label: "Notes" },
  ];

  return (
    <Entity360Shell type="fund" title={entity.name || entity.fund_name || "Fund"} subtitle={`${kpiData.fund_type || ""} Fund`}
      status={kpiData.status} kpis={kpis} tabs={tabs} activeTab={tab} onTabChange={setTab}
      onBack={onBack} breadcrumbs={crumbs} onNavigate={navigateToEntity} relatedEntities={relatedEntities}>

      {tab === "overview" && (
        <div className="space-y-4" data-testid="fund-overview">
          <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
            <h3 className="text-xs font-bold text-[#222] mb-3">Fund Details</h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-[#999]">Fund Type:</span> <span className="font-medium text-[#333]">{entity.fund_type || "—"}</span></div>
              <div><span className="text-[#999]">Management Fee:</span> <span className="font-medium text-[#333]">{kpiData.management_fee_pct || 0}%</span></div>
              <div><span className="text-[#999]">Carry:</span> <span className="font-medium text-[#333]">{entity.carry_pct || 0}%</span></div>
              <div><span className="text-[#999]">Min Commitment:</span> <span className="font-medium text-[#333]">{fmtUSD((entity.min_commitment_cents || 0) / 100)}</span></div>
              <div><span className="text-[#999]">Auto Allocate:</span> <span className="font-medium text-[#333]">{entity.auto_allocate_enabled ? "Yes" : "No"}</span></div>
              <div><span className="text-[#999]">Created:</span> <span className="font-medium text-[#333]">{entity.created_at ? new Date(entity.created_at).toLocaleDateString() : "—"}</span></div>
            </div>
          </div>

          {financials && (
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
              <h3 className="text-xs font-bold text-[#222] mb-3">Capital Summary</h3>
              <div className="h-3 rounded-full bg-[#e8e0d0] overflow-hidden mb-2">
                <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, pct)}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-[#999]">
                <span>{fmtUSD(currentDollars)} raised</span>
                <span>{pct}% of {fmtUSD(targetDollars)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "deals" && (
        <div className="space-y-2" data-testid="fund-deals">
          {(relationships.deals || []).length === 0 ? (
            <div className="text-center py-8 text-sm text-[#888]">No deals allocated to this fund</div>
          ) : (relationships.deals || []).map(d => (
            <div key={d.entity_id} className="rounded-lg border border-[#e8e0d0] bg-white p-3 flex items-center justify-between hover:shadow-sm transition-shadow">
              <EntityLink type="deal" id={d.entity_id} label={d.label} sublabel={d.sublabel} onClick={navigateToEntity} />
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">{d.status}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "timeline" && (
        <div className="space-y-2" data-testid="fund-timeline">
          {timeline.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#888]">No timeline events yet</div>
          ) : timeline.map((e, i) => (
            <div key={e.event_id || i} className="rounded-lg border border-[#e8e0d0] bg-white p-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-[#333]">{e.event_type?.replace(/_/g, " ")}</span>
                <span className="text-[10px] text-[#999]">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</span>
              </div>
              {e.summary && <p className="text-[11px] text-[#666] mt-1">{e.summary}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === "comms" && (
        <EntityComms entityType="fund" entityId={fundId} />
      )}

      {tab === "notes" && (
        <EntityNotes entityType="fund" entityId={fundId} onNavigate={navigateToEntity} />
      )}
    </Entity360Shell>
  );
}
